<template>
  <b-navbar
    type="dark"
    variant="primary"
    class="mb-2"
  >
    <b-navbar-brand>Administration DOMICIALIZ</b-navbar-brand>
    <b-navbar-nav class="mr-auto">
      <b-nav-item
        :to="{ name: 'admin-dom-dossiers' }"
        :active="active === 'dossiers'"
      >
        Dossiers
      </b-nav-item>
      <b-nav-item
        :to="{ name: 'admin-dom-clients' }"
        :active="active === 'clients'"
      >
        Clients
      </b-nav-item>
      <b-nav-item
        :to="{ name: 'admin-dom-reservation' }"
        :active="active === 'reservation'"
      >
        Réservation
      </b-nav-item>
      <b-nav-item
        v-if="admin && admin.superadmin"
        :to="{ name: 'admin-dom-utilisateurs' }"
        :active="active === 'utilisateurs'"
      >
        Utilisateurs
      </b-nav-item>
      <b-nav-item
        v-if="admin && admin.superadmin"
        :to="{ name: 'admin-dom-formules' }"
        :active="active === 'formules'"
      >
        Formules
      </b-nav-item>
      <b-nav-item
        v-if="admin && admin.superadmin"
        :to="{ name: 'admin-dom-documents-requis' }"
        :active="active === 'documents-requis'"
      >
        Documents
      </b-nav-item>
      <b-nav-item
        v-if="admin && admin.superadmin"
        :to="{ name: 'admin-dom-formes-juridiques' }"
        :active="active === 'formes-juridiques'"
      >
        Formes juridiques
      </b-nav-item>
      <b-nav-item
        v-if="admin && admin.superadmin"
        :to="{ name: 'admin-dom-templates' }"
        :active="active === 'templates'"
      >
        Templates
      </b-nav-item>
      <b-nav-item
        :to="{ name: 'admin-dom-documentation' }"
        :active="active === 'documentation'"
      >
        Documentation
      </b-nav-item>
      <b-nav-item
        :to="{ name: 'admin-dom-profil' }"
        :active="active === 'profil'"
      >
        Mon profil
      </b-nav-item>
    </b-navbar-nav>
    <b-navbar-nav class="ml-auto align-items-center">
      <span
        v-if="admin"
        class="text-white mr-2"
      >
        {{ admin.prenom }} {{ admin.nom }}
        <b-badge
          v-if="admin.superadmin"
          variant="warning"
          class="ml-1"
        >
          superadmin
        </b-badge>
      </span>
      <!-- Version déployée : permet de vérifier d'un coup d'œil quel build
           est réellement servi, et si le schéma de base suit le code. -->
      <span
        class="text-white-50 small mr-2 text-right"
        style="line-height: 1.2;"
        :title="versionDetail"
      >
        <span class="d-block">
          <b-badge
            v-if="envApp && envApp !== 'production'"
            variant="warning"
            class="mr-1"
          >
            {{ envApp }}
          </b-badge>
          app {{ versionApp }}
        </span>
        <span class="d-block">
          api {{ versionApi }}
          <b-badge
            v-if="stripeTest"
            variant="warning"
            class="ml-1"
          >
            Stripe test
          </b-badge>
          <!-- L'environnement GoCardless était renvoyé par l'API mais jamais
               affiché. Un admin sur la recette voyait « test » et « Stripe
               test », rien qui dise qu'une relance de mandat enverrait un
               vrai email à un vrai client avec un lien de test — la base
               étant partagée. Incident du 30/10/2026. -->
          <b-badge
            v-if="gocardlessEnv && gocardlessEnv !== 'live'"
            variant="warning"
            class="ml-1"
          >
            GoCardless {{ gocardlessEnv }}
          </b-badge>
          <b-badge
            v-if="schemaEnRetard"
            variant="danger"
            class="ml-1"
          >
            schéma en retard
          </b-badge>
          <!-- Une variable d'environnement absente est une panne
               silencieuse : le code se charge, les pages s'affichent, et
               c'est le premier client qui découvre que rien ne part. -->
          <b-badge
            v-if="configBloquants > 0"
            variant="danger"
            class="ml-1"
            style="cursor: pointer;"
            @click="ouvrirConfig"
          >
            configuration : {{ configBloquants }}
          </b-badge>
          <b-badge
            v-else-if="configDegrades > 0"
            variant="warning"
            class="ml-1"
            style="cursor: pointer;"
            @click="ouvrirConfig"
          >
            configuration : {{ configDegrades }}
          </b-badge>
          <b-badge
            v-else-if="configGroupes.length"
            variant="success"
            class="ml-1"
            style="cursor: pointer;"
            @click="ouvrirConfig"
          >
            configuration
          </b-badge>
        </span>
      </span>
      <b-button
        size="sm"
        variant="outline-light"
        @click="logout"
      >
        Déconnexion
      </b-button>
    </b-navbar-nav>

    <!-- Détail de la configuration de l'instance qui répond. L'API ne
         renvoie jamais les VALEURS, seulement « posée / absente / valeur par
         défaut » — voir api_reunibox/helpers/diagnosticConfig.js. -->
    <b-modal
      id="modal-config-api"
      title="Configuration de l'API"
      size="lg"
      ok-only
      ok-title="Fermer"
      scrollable
    >
      <p class="text-muted small">
        Réglages de l'instance <strong>{{ cibleApi || urlApi }}</strong>.
        Aucune valeur n'est transmise : seulement l'état de chaque réglage.
      </p>
      <div
        v-for="groupe in configGroupes"
        :key="groupe.nom"
        class="mb-2"
      >
        <h6 class="mb-50">
          {{ groupe.nom }}
        </h6>
        <b-table-simple
          small
          borderless
          class="mb-1"
        >
          <b-tbody>
            <b-tr
              v-for="c in groupe.controles"
              :key="c.cle"
            >
              <b-td style="width: 1%;white-space: nowrap;">
                <b-badge :variant="varianteControle(c)">
                  {{ libelleControle(c) }}
                </b-badge>
              </b-td>
              <b-td style="width: 1%;white-space: nowrap;">
                <code>{{ c.cle }}</code>
              </b-td>
              <b-td class="text-muted small">
                {{ c.effet }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </b-modal>
  </b-navbar>
</template>

<script>
import {
  BNavbar,
  BNavbarBrand,
  BNavbarNav,
  BNavItem,
  BButton,
  BBadge,
  BModal,
  BTableSimple,
  BTbody,
  BTr,
  BTd,
} from 'bootstrap-vue'

export default {
  components: {
    BNavbar,
    BNavbarBrand,
    BNavbarNav,
    BNavItem,
    BButton,
    BBadge,
    BModal,
    BTableSimple,
    BTbody,
    BTr,
    BTd,
  },
  props: {
    active: { type: String, default: '' },
  },
  data() {
    return {
      admin: null,
      // Injectées au build par .github/workflows/_build-app.yml. Vides si le
      // build a été fait à la main, ce qui est en soi une information.
      versionApp: process.env.VUE_APP_VERSION || 'locale',
      dateApp: process.env.VUE_APP_BUILD_DATE || '',
      envApp: process.env.VUE_APP_ENV || '',
      versionApi: '…',
      dateApi: '',
      deploiementApi: '',
      schemaEnRetard: false,
      urlApi: process.env.VUE_APP_APIURL || 'https://api.reunibox.com/',
      // Renvoyés par GET /admindom/version : ce que fait réellement l'API
      // qui répond, et non ce que le build croit.
      stripeTest: false,
      gocardlessPret: null,
      gocardlessEnv: '',
      cibleApi: '',
      // Diagnostic de la configuration, renvoyé par la même requête.
      configGroupes: [],
      configBloquants: 0,
      configDegrades: 0,
    }
  },
  computed: {
    // Détail au survol : les dates et le mode de déploiement de l'API, qui
    // n'ont pas leur place dans une barre de navigation.
    versionDetail() {
      const lignes = [`Application : ${this.versionApp}${this.dateApp ? ` (${this.dateApp})` : ''}`]
      lignes.push(`API : ${this.versionApi}${this.dateApi ? ` (${this.dateApi})` : ''}`)
      // Quelle API répond : sur un build de recette, c'est la vérification la
      // plus utile — un front de test qui taperait sur l'API de production
      // encaisserait de vrais paiements.
      lignes.push(`Adresse de l'API : ${this.urlApi}`)
      if (this.deploiementApi === 'manuel') {
        lignes.push('API déposée à la main, hors du workflow de déploiement')
      }
      if (this.cibleApi) lignes.push(`Instance : ${this.cibleApi}`)
      if (this.stripeTest) {
        lignes.push('Stripe DOMICIALIZ en mode TEST : aucun paiement réel')
      }
      if (this.gocardlessPret === false) {
        lignes.push('GoCardless non configuré : les demandes de mandat répondront « indisponible »')
      }
      if (this.gocardlessEnv && this.gocardlessEnv !== 'live') {
        lignes.push('GoCardless en bac à sable : un lien de mandat engendré ici est un lien de TEST,'
          + ' alors que la base est partagée avec la production')
      }
      if (this.schemaEnRetard) {
        lignes.push('Une migration SQL n\'est pas appliquée : certains écrans peuvent être incomplets')
      }
      if (this.configBloquants > 0) {
        lignes.push(`${this.configBloquants} réglage(s) manquant(s) bloquent une fonction — cliquer le badge`)
      } else if (this.configDegrades > 0) {
        lignes.push(`${this.configDegrades} réglage(s) manquant(s) dégradent une fonction — cliquer le badge`)
      }
      return lignes.join('\n')
    },
  },
  created() {
    const raw = localStorage.getItem('domAdminData')
    if (raw) this.admin = JSON.parse(raw)
    this.chargerVersionApi()
  },
  methods: {
    chargerVersionApi() {
      const headers = { 'x-access-token': localStorage.getItem('domAdminToken') }
      this.$http.get('/admindom/version', { headers })
        .then(response => {
          const { data } = response
          if (data.res !== 1) return
          this.versionApi = (data.api && data.api.commit) || 'inconnue'
          this.dateApi = (data.api && data.api.date) || ''
          this.deploiementApi = (data.api && data.api.deploiement) || ''
          this.schemaEnRetard = data.schemaAJour === false
          this.cibleApi = (data.api && data.api.cible) || ''
          const services = data.services || {}
          this.stripeTest = services.stripeDomTest === true
          this.gocardlessPret = services.gocardless === undefined ? null : services.gocardless
          this.gocardlessEnv = services.gocardlessEnv || ''
          const config = data.configuration || {}
          this.configGroupes = config.groupes || []
          this.configBloquants = config.bloquants || 0
          this.configDegrades = config.degrades || 0
        })
        .catch(() => {
          // API injoignable ou endpoint absent (version antérieure déployée) :
          // on le dit plutôt que de laisser des points de suspension.
          this.versionApi = 'injoignable'
        })
    },

    ouvrirConfig() {
      this.$bvModal.show('modal-config-api')
    },

    // Un réglage « posé » n'est pas une alerte : on ne colore en rouge ou en
    // orange que ce qui manque, et selon ce que son absence casse.
    varianteControle(c) {
      if (c.etat === 'ok') return 'light-success'
      if (c.etat === 'defaut') return 'light-secondary'
      return c.gravite === 'bloquant' ? 'light-danger' : 'light-warning'
    },

    libelleControle(c) {
      if (c.etat === 'ok') return 'posé'
      if (c.etat === 'defaut') return 'défaut'
      return c.gravite === 'bloquant' ? 'manquant' : 'absent'
    },

    logout() {
      localStorage.removeItem('domAdminToken')
      localStorage.removeItem('domAdminData')
      this.$router.push({ name: 'admin-dom-login' })
    },
  },
}
</script>
